import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import cartAPI from "../api/cartApi";
import { showError, showSuccess } from "../utils/toastUtils";

export const CartContext = createContext();

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);
  const [cartSummary, setCartSummary] = useState({
    subtotal: "",
    discount: "",
    shipping: "",
    total: "",
    couponCode: "",
  });

  // Jab tak server "add" confirm nahi karta, tab tak count turant badh jaye
  const [pendingAdds, setPendingAdds] = useState(0);

  const fetchCart = useCallback(async () => {
    try {
      const res = await cartAPI.fetchCart();
      if (res.code === 200) {
        const result = res.data;

        setCartItems([...(result?.items || [])]);
        setCartSummary({ ...(result?.summary || {}) });
      }
    } catch (err) {
      console.log("No Data");
    }
  }, []);

  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  // true => add ho gaya, false => fail. Caller await na bhi kare to chalega.
  const addToCart = useCallback(
    async (productId, quantity = 1, variantId = null, customization = {}) => {
      const payload = {
        productId,
        quantity,
        variantId,
        customization,
      };

      setPendingAdds((p) => p + 1); // ← instant feedback

      try {
        const res = await cartAPI.addCart(payload);

        if (res.code === 200) {
          const result = res.data;

          showSuccess(res.message);

          if (!localStorage.getItem("x-guest-session-id") && result?.sessionId) {
            localStorage.setItem("x-guest-session-id", result.sessionId);
          }

          // cart refresh hone tak pending count rakho, flicker nahi hoga
          await fetchCart();
          return true;
        }

        showError(res.message);
        return false;
      } catch (err) {
        showError(err?.response?.data?.message || "Could not add to cart");
        return false;
      } finally {
        setPendingAdds((p) => Math.max(0, p - 1));
      }
    },
    [fetchCart],
  );

  const updateQuantity = useCallback(
    async (itemId, quantity) => {
      // UI turant update — server fail ho to fetchCart se wapas sahi ho jayega
      setCartItems((prev) =>
        prev.map((i) =>
          i._id === itemId
            ? {
                ...i,
                quantity,
                subtotal: Number(i.price || 0) * quantity,
              }
            : i,
        ),
      );

      try {
        const res = await cartAPI.updateQuantity(itemId, { quantity });
        if (res.code !== 200) showError(res.message);
      } catch (err) {
        showError(err?.response?.data?.message);
      } finally {
        fetchCart();
      }
    },
    [fetchCart],
  );

  const removeItem = useCallback(
    async (id) => {
      // item turant list se hata do
      setCartItems((prev) => prev.filter((i) => i._id !== id));

      try {
        const res = await cartAPI.removeItem(id);
        if (res.code !== 200) showError(res.message);
      } catch (err) {
        // fetchCart neeche item wapas la dega
      } finally {
        fetchCart();
      }
    },
    [fetchCart],
  );

  const cartCount = cartItems.length + pendingAdds;

  const value = useMemo(
    () => ({
      cartItems,
      cartSummary,
      cartCount,
      addToCart,
      fetchCart,
      updateQuantity,
      removeItem,
    }),
    [
      cartItems,
      cartSummary,
      cartCount,
      addToCart,
      fetchCart,
      updateQuantity,
      removeItem,
    ],
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => useContext(CartContext);
